import { useCallback, useEffect, useState } from "react"
import useEmblaCarousel from "embla-carousel-react"
import type { EmblaCarouselType } from "embla-carousel"
import type { ReactNode, CSSProperties } from "react"

export type PeekValue = "none" | "small" | "medium" | "large"

export type Breakpoint = "base" | "sm" | "md" | "lg" | "xl"

export type ResponsivePeek = PeekValue | Partial<Record<Breakpoint, PeekValue>>

export type ResponsiveArrows = boolean | { mobile?: boolean; desktop?: boolean }

export type ArrowPosition = "side" | "top-right"

export type ResponsivePaginationDots =
  | boolean
  | { mobile?: boolean; desktop?: boolean }

export type SlidesToShowValue = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10

export type ResponsiveSlidesToShow =
  | SlidesToShowValue
  | Partial<Record<Breakpoint, SlidesToShowValue>>

const PEEK_MAP: Record<PeekValue, number> = {
  none: 0,
  small: 24,
  medium: 48,
  large: 72
}

const BREAKPOINTS: Breakpoint[] = ["base", "sm", "md", "lg", "xl"]

/** Resolves a responsive value map into a fully-populated record by cascading
 *  smaller breakpoint values forward when a larger one is omitted. */
function resolveBreakpoints<T>(
  value: T | Partial<Record<Breakpoint, T>>,
  defaultValue: T
): Record<Breakpoint, T> {
  if (typeof value !== "object" || value === null) {
    const scalar = value as T
    return { base: scalar, sm: scalar, md: scalar, lg: scalar, xl: scalar }
  }
  const map = value as Partial<Record<Breakpoint, T>>
  let current = map.base ?? defaultValue
  return BREAKPOINTS.reduce(
    (acc, bp) => {
      current = map[bp] ?? current
      acc[bp] = current
      return acc
    },
    {} as Record<Breakpoint, T>
  )
}

function slideWidth(slides: number, peekPx: number, gap: number): string {
  const gapTotal = `${gap * (slides - 1)}px`
  return peekPx > 0
    ? `calc((100% - ${gapTotal} - ${peekPx}px) / ${slides})`
    : `calc((100% - ${gapTotal}) / ${slides})`
}

export interface CarouselContainerProps {
  children: ReactNode
  slidesToShow?: ResponsiveSlidesToShow
  peek?: ResponsivePeek
  gap?: number
  onInit?: (embla: EmblaCarouselType) => void
  showArrows?: boolean
  arrowPosition?: ArrowPosition
  className?: string
}

const ArrowButton = ({
  onClick,
  disabled,
  direction
}: {
  onClick: () => void
  disabled: boolean
  direction: "prev" | "next"
}) => (
  <button
    onClick={onClick}
    disabled={disabled}
    aria-label={direction === "prev" ? "Previous slide" : "Next slide"}
    className="flex items-center justify-center w-9 h-9 rounded-full border border-gray-200 bg-white shadow-sm transition-opacity disabled:opacity-30 hover:bg-gray-50 disabled:cursor-not-allowed"
  >
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
    >
      {direction === "prev" ? (
        <path
          d="M10 3L5 8L10 13"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ) : (
        <path
          d="M6 3L11 8L6 13"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}
    </svg>
  </button>
)

const CarouselContainer = ({
  children,
  slidesToShow = 3,
  peek = "none",
  gap = 16,
  onInit,
  showArrows = false,
  arrowPosition = "side",
  className
}: CarouselContainerProps) => {
  const slides = resolveBreakpoints<SlidesToShowValue>(slidesToShow, 3)
  const peeks = resolveBreakpoints<PeekValue>(peek, "none")
  const [canScrollPrev, setCanScrollPrev] = useState(false)
  const [canScrollNext, setCanScrollNext] = useState(false)

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    align: "start"
  })

  const updateScrollState = useCallback((embla: EmblaCarouselType) => {
    setCanScrollPrev(embla.canScrollPrev())
    setCanScrollNext(embla.canScrollNext())
  }, [])

  const handleInit = useCallback(
    (embla: EmblaCarouselType) => {
      updateScrollState(embla)
      if (onInit) {
        onInit(embla)
      }
    },
    [onInit, updateScrollState]
  )

  useEffect(() => {
    if (!emblaApi) return
    handleInit(emblaApi)
    emblaApi.on("select", updateScrollState)
    emblaApi.on("reInit", updateScrollState)
    return () => {
      emblaApi.off("select", updateScrollState)
      emblaApi.off("reInit", updateScrollState)
    }
  }, [emblaApi, handleInit, updateScrollState])

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi])
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi])

  const containerStyle: CSSProperties = {
    "--carousel-slide-width-base": slideWidth(
      slides.base,
      PEEK_MAP[peeks.base],
      gap
    ),
    "--carousel-slide-width-sm": slideWidth(slides.sm, PEEK_MAP[peeks.sm], gap),
    "--carousel-slide-width-md": slideWidth(slides.md, PEEK_MAP[peeks.md], gap),
    "--carousel-slide-width-lg": slideWidth(slides.lg, PEEK_MAP[peeks.lg], gap),
    "--carousel-slide-width-xl": slideWidth(slides.xl, PEEK_MAP[peeks.xl], gap),
    "--carousel-gap": `${gap}px`
  } as CSSProperties

  const viewport = (
    <div
      className={`overflow-hidden${className ? ` ${className}` : ""}`}
      ref={emblaRef}
      style={containerStyle}
    >
      <div
        className="flex items-stretch [&>*]:flex-none [&>*]:min-w-0 [&>*]:w-[var(--carousel-slide-width-base)] sm:[&>*]:w-[var(--carousel-slide-width-sm)] md:[&>*]:w-[var(--carousel-slide-width-md)] lg:[&>*]:w-[var(--carousel-slide-width-lg)] xl:[&>*]:w-[var(--carousel-slide-width-xl)]"
        style={{ gap: `${gap}px` }}
      >
        {children}
      </div>
    </div>
  )

  if (!showArrows) return viewport

  if (arrowPosition === "top-right") {
    return (
      <div>
        <div className="flex justify-end gap-2 mb-3">
          <ArrowButton
            direction="prev"
            onClick={scrollPrev}
            disabled={!canScrollPrev}
          />
          <ArrowButton
            direction="next"
            onClick={scrollNext}
            disabled={!canScrollNext}
          />
        </div>
        {viewport}
      </div>
    )
  }

  // side arrows
  return (
    <div className="relative">
      <button
        onClick={scrollPrev}
        disabled={!canScrollPrev}
        aria-label="Previous slide"
        className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 z-10 flex items-center justify-center w-9 h-9 rounded-full border border-gray-200 bg-white shadow-sm transition-opacity disabled:opacity-0 hover:bg-gray-50 disabled:pointer-events-none"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M10 3L5 8L10 13"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {viewport}
      <button
        onClick={scrollNext}
        disabled={!canScrollNext}
        aria-label="Next slide"
        className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 z-10 flex items-center justify-center w-9 h-9 rounded-full border border-gray-200 bg-white shadow-sm transition-opacity disabled:opacity-0 hover:bg-gray-50 disabled:pointer-events-none"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M6 3L11 8L6 13"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
    </div>
  )
}

CarouselContainer.displayName = "CarouselContainer"

export { CarouselContainer }
export default CarouselContainer
